import {
  ETABLISSEMENT_FICHIER_STATUS_STYLES,
  ETAT_ADMINISTRATIF_TEXTS,
  SUIVI_DEPOT_DEFAULT_FILTERS,
  SuiviDepotFilters,
  useTextes,
} from '@common/config';
import { useOfaDetails } from '@common/hooks/organisme';
import {
  EtablissementProjection,
  EtablissementSuiviDepotDto,
  Page,
  Sorter,
  StatutDeclaration,
  useDownloadOrganismeEtablissementFichierMutation,
  useDownloadOrganismeEtablissementGeneratedDeclarationMutation,
  useGetOrganismeEtablissementsQuery,
} from '@common/services';
import { EtablissementFichierSuiviDepotDto, EtablissementFichierType } from '@common/services/etablissement-fichier';
import { FcPaginator, FcTable, FcTableResult, FileBadge } from '@common/ui';
import { StatusBadge } from '@common/ui/badge';
import { TypeDeclarationCell } from '@common/ui/etablissement';
import { CommonSuiviDepotFilters } from '@common/ui/filters/etablissement';
import { DateUtils, generateDeclarationAttestationFileName } from '@common/utils';
import { ALERTE_FILE_NAME } from '@common/utils/src/organisme/organisme.utils';
import React, { useEffect, useState } from 'react';
import { RiDownloadFill } from 'react-icons/ri';

const DEFAULT_SORTERS: Sorter[] = [
  { field: 'principal', direction: 'desc' },
  { field: 'typeDeclaration', direction: 'asc' },
  { field: 'siret', direction: 'asc' },
];

export const SuiviDepot = ({ campagneAnnee, organismeId }: SuiviDepotProps) => {
  const t = useTextes('ParcoursOfa', 'Campagne');

  const { organisme } = useOfaDetails({ id: organismeId });

  const [page, setPage] = useState<number>(0);
  const [sorter, setSorter] = useState<Sorter | null>(null);
  const [filters, setFilters] = useState<SuiviDepotFilters>(SUIVI_DEPOT_DEFAULT_FILTERS);

  const [downloadGeneratedDeclaration] = useDownloadOrganismeEtablissementGeneratedDeclarationMutation();
  const [downloadEtablissementFichier] = useDownloadOrganismeEtablissementFichierMutation();

  const { data: pageEtablissements = {} as Page<EtablissementSuiviDepotDto> } = useGetOrganismeEtablissementsQuery({
    id: organismeId,
    projection: EtablissementProjection.SUIVI_DEPOT,
    page,
    sorters: (sorter) ? [sorter] : DEFAULT_SORTERS,
    filters: {
      ...filters,
      declarant: true,
      'organisme.campagne.annee': campagneAnnee,
    },
  }, {
    skip: !organismeId || !campagneAnnee,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    setPage(0);
  }, [filters]);

  return (
    <div className={'flex flex-col px-8 py-6'}>

      <h6>{t['DEPOT_DEMANDE_TEXTE1']}</h6>

      <div className={'flex flex-col gap-6'}>

        <CommonSuiviDepotFilters filters={filters} setFilters={setFilters} />

        <div className={'separator'} />

        <FcTableResult count={pageEtablissements?.totalElements}
                       mapper={count => `${count} Établissement${(count > 1) ? 's' : ''} déclarant${(count > 1) ? 's' : ''}`} />

        <FcTable<EtablissementSuiviDepotDto>
          columnsConfig={[
            {
              header: { title: 'État', sorting: true },
              field: 'etat',
              render: (etat) => {
                if (!etat) return '';

                return (
                  <div className={'flex'}>
                    <div className={'p-2 shadow'}>
                      <span>{ETAT_ADMINISTRATIF_TEXTS[etat]}</span>
                    </div>
                  </div>
                );
              },
            },
            {
              header: { title: 'SIRET', sorting: true },
              field: 'siret',
            },
            {
              header: { title: 'Dénomination', sorting: true },
              field: 'denominationUsuelle',
            },
            {
              header: { title: 'Type déclaration', sorting: true },
              field: 'typeDeclaration',
              render: (type, etab) => <TypeDeclarationCell organismeId={organismeId}
                                                           principal={etab.principal}
                                                           typeDeclaration={type} />,
            },
            {
              header: { title: 'Statut déclaration', sorting: true },
              field: 'statutDeclaration',
              render: (statut: string) => {
                if (!statut) return '';

                const { text, textColor, borderColor } = ETABLISSEMENT_FICHIER_STATUS_STYLES[statut] ?? {};
                return <StatusBadge text={text} borderColor={borderColor} textColor={textColor} />;
              },
            },
            {
              header: { title: 'Statut attestation', sorting: true },
              field: 'statutAttestation',
              render: (statut: string) => {
                if (!statut) return '';

                const { text, textColor, borderColor } = ETABLISSEMENT_FICHIER_STATUS_STYLES[statut] ?? {};
                return <StatusBadge text={text} borderColor={borderColor} textColor={textColor} />;
              },
            },
            {
              header: { title: 'Déclaration' },
              field: 'fichiers',
              render: (fichiers: EtablissementFichierSuiviDepotDto[], element) => {
                const declaration = fichiers.find(({ type }) => (type === EtablissementFichierType.DECLARATION));
                if (!declaration) return '';

                const isDeclarationDepositInvalid = (element.statutDeclaration === StatutDeclaration.PARTIELLE);

                return (
                  <FileBadge text={DateUtils.formatDdMmYyyy(declaration.dateModification)}
                             iconColor={(isDeclarationDepositInvalid) ? '#E1000F' : 'var(--background-flat-blue-france)'}
                             onClick={() => downloadEtablissementFichier({
                               id: organismeId,
                               etablissementId: element.id,
                               fichierType: EtablissementFichierType.DECLARATION,
                               defaultFilename: generateDeclarationAttestationFileName(
                                 organisme.siren, organisme.raisonSociale,
                                 campagneAnnee, 'Déclaration'),
                               forceDefaultFilename: true,
                             })} />
                );
              },
            },
            {
              header: { title: 'Fichier d\'alerte' },
              field: 'fichiers',
              render: (fichiers: EtablissementFichierSuiviDepotDto[], element) => {
                const alerte = fichiers.find(({ type }) => (type === EtablissementFichierType.ALERTES));
                if (!alerte) return '';

                return (
                  <FileBadge text={DateUtils.formatDdMmYyyy(alerte.dateModification)}
                             iconColor={'#E1000F'}
                             onClick={() => downloadEtablissementFichier({
                               id: organismeId,
                               etablissementId: element.id,
                               fichierType: EtablissementFichierType.ALERTES,
                               defaultFilename: ALERTE_FILE_NAME,
                               forceDefaultFilename: true,
                             })} />
                );
              },
            },
            {
              header: { title: 'Attestation' },
              field: 'fichiers',
              render: (fichiers: EtablissementFichierSuiviDepotDto[], element) => {
                const attestation = fichiers.find(({ type }) => (type === EtablissementFichierType.ATTESTATION));
                if (!attestation) return '';

                return (
                  <FileBadge text={DateUtils.formatDdMmYyyy(attestation.dateModification)}
                             iconColor={'var(--background-flat-blue-france)'}
                             onClick={() => downloadEtablissementFichier({
                               id: organismeId,
                               etablissementId: element.id,
                               fichierType: EtablissementFichierType.ATTESTATION,
                               defaultFilename: generateDeclarationAttestationFileName(
                                 organisme.siren, organisme.raisonSociale,
                                 campagneAnnee, 'Attestation'),
                               forceDefaultFilename: true,
                             })} />
                );
              },
            },
            {
              header: { title: 'Document Travail' },
              field: 'id',
              render: (id) => <RiDownloadFill className={'file-view-icon'}
                                              onClick={() => downloadGeneratedDeclaration({
                                                id: organismeId,
                                                etablissementId: id,
                                                travail: true,
                                              })} />,
            },
          ]}
          sorter={sorter}
          onSorterChange={setSorter}
          data={pageEtablissements?.content ?? []} />

        {(pageEtablissements?.totalPages > 1) && (
          <div className={'flex flex-col items-center'}>
            <FcPaginator page={pageEtablissements?.pageable.pageNumber ?? 0}
                         totalPages={pageEtablissements?.totalPages ?? 0}
                         onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
};

interface SuiviDepotProps {
  campagneAnnee: number;
  organismeId: number;
}
